




































































































































































































































































import { AxiosError } from 'axios';
import moment from 'moment';
import { mapActions } from 'vuex';
import {
  IInventoryAdvancePayment,
  IMentionAttachmentComment,
  InventoryAdvancePaymentComment,
  InventoryAdvancePaymentCommentAttachment
} from '@/models';
import { get } from 'vuex-pathify';
import { cloneDeep } from 'lodash';
import mixins from '@/utils/mixins';
import auth from '@/mixins/auth';
import snackbar from '@/mixins/snackbar';
import { PropType } from 'vue';
import { IAttachment, IUser } from '@/models';
import { ErrorManager, ISystemError } from '@/models/error';

import {
  createFile,
  diffDeep,
  isHtmlTextEmpty,
  setErrorFromAxiosError
} from '@/utils/helpers';

import CommentAttachmentList from '../common/CommentAttachmentList.vue';
import MentionProvider from '../mention/MentionProvider.vue';
import QuillEditor from '../common/QuillEditor.vue';
import DeleteConfirmationDialog from '../common/DeleteConfirmationDialog.vue';

export default mixins(auth, snackbar).extend({
  components: {
    CommentAttachmentList,
    DeleteConfirmationDialog,
    MentionProvider,
    QuillEditor
  },

  props: {
    inventoryAdvancePayment: {
      type: Object as PropType<IInventoryAdvancePayment>,
      required: true
    },

    inventoryAdvancePaymentComment: {
      type: Object as () => IMentionAttachmentComment,
      required: true
    }
  },

  name: 'InventoryAdvancePaymentCommentListItem',

  data() {
    return {
      loading: false,
      isEditing: false,

      commentLoading: false,
      commentErrorMessage: '',
      commentDetailErrorMessage: '',

      // delete comment
      deletePaymentCommentErrorMessage: null as ISystemError | null,
      commentPaymentDeleteInProgress: false,
      selectedComment: cloneDeep(this.inventoryAdvancePaymentComment),
      deleteInventoryAdvancePaymentCommentDialog: false,

      commentTextHtml: this.inventoryAdvancePaymentComment.getTextHtml(),
      mentionsLoading: false,

      deleteCommentAttachmentDialog: false,
      isAttachmentDeleteInProgress: false,
      attachmentDeleteError: null as ISystemError | null,
      selectedAttachment: null as null | IAttachment,
      attachments: [] as IAttachment[]
    };
  },

  computed: {
    isInventoryAdvancePaymentCommentOwner(): boolean {
      return (
        this.getCurrentUserFullName ===
        this.inventoryAdvancePaymentComment.owner
      );
    },

    canSaveComment(): boolean {
      return (
        !isHtmlTextEmpty(this.selectedComment.text) && !this.commentLoading
      );
    },

    mentionableUsers: get<IUser[]>(
      'inventoryAdvancePayment/getInventoryAdvancePaymentMentionableUsers'
    )
  },

  methods: {
    ...mapActions({
      updateInventoryAdvancePaymentComment:
        'inventoryAdvancePayment/updateInventoryAdvancePaymentComment',
      deleteInventoryAdvancePaymentComment:
        'inventoryAdvancePayment/deleteInventoryAdvancePaymentComment',
      fetchInventoryAdvancePaymentCommentsMentionableUsers:
        'inventoryAdvancePayment/fetchInventoryAdvancePaymentCommentsMentionableUsers',
      addInventoryAdvancePaymentCommentAttachments:
        'inventoryAdvancePayment/addInventoryAdvancePaymentCommentAttachments',
      removeInventoryAdvancePaymentCommentAttachment:
        'inventoryAdvancePayment/removeInventoryAdvancePaymentCommentAttachment'
    }),

    timeFromNow: (date: Date | string) => {
      return moment(date).fromNow();
    },

    getInitials(name: string) {
      return name
        .toUpperCase()
        .split(' ')
        .map(n => n[0])
        .join('');
    },

    textHasDifference(): boolean {
      return (
        this.selectedComment.text === this.inventoryAdvancePaymentComment.text
      );
    },

    startPaymentEditingComment() {
      // To reset the selected inventoryAdvancePayment comment data to the original when rediting
      this.selectedComment = cloneDeep(this.inventoryAdvancePaymentComment);
      this.isEditing = true;
    },

    stopPaymentEditingComment() {
      if (!this.selectedComment.text || this.textHasDifference()) {
        this.isEditing = false;
      }
    },

    clearAllErrors(): void {
      this.deletePaymentCommentErrorMessage = null;
      this.commentErrorMessage = '';
      this.attachmentDeleteError = null;
    },

    cancelInventoryAdvancePaymentCommentDeleteDialog() {
      this.deleteInventoryAdvancePaymentCommentDialog = false;
    },

    removeInventoryAdvancePaymentComment() {
      this.clearAllErrors();
      this.commentPaymentDeleteInProgress = true;
      this.deleteInventoryAdvancePaymentComment({
        inventoryAdvancePaymentId: this.inventoryAdvancePayment.id,
        commentId: this.inventoryAdvancePaymentComment.id
      })
        .then(() => {
          this.deleteInventoryAdvancePaymentCommentDialog = false;
        })
        .catch(error => {
          this.deletePaymentCommentErrorMessage = setErrorFromAxiosError(
            error as AxiosError
          );
        })
        .finally(() => {
          this.commentPaymentDeleteInProgress = false;
        });
    },

    editComment() {
      this.clearAllErrors();

      this.commentLoading = true;

      const parsedCommentMentionText = InventoryAdvancePaymentComment.parseHtmlToMentionText(
        this.commentTextHtml
      );

      this.selectedComment.text = parsedCommentMentionText;

      const commentDelta = diffDeep(
        this.selectedComment,
        this.inventoryAdvancePaymentComment,
        true
      );

      return this.updateInventoryAdvancePaymentComment({
        inventoryAdvancePaymentId: this.inventoryAdvancePayment.id,
        comment: commentDelta
      })
        .then(async () => {
          if (this.attachments.length !== 0) {
            await this.addInventoryAdvancePaymentCommentAttachments({
              inventoryAdvancePaymentId: this.inventoryAdvancePayment.id,
              commentId: commentDelta.id,
              attachments: this.attachments
            });
            this.attachments = [];
          }
          this.selectedComment = cloneDeep(this.inventoryAdvancePaymentComment);
          this.isEditing = false;
        })
        .catch(error => {
          if (error.response) {
            if (error.response.data.detail) {
              this.commentDetailErrorMessage = error.response.data.detail;
            }
          }
          this.commentErrorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.commentLoading = false;
        });
    },

    async loadMentionableUsers() {
      this.clearAllErrors();

      this.mentionsLoading = true;
      try {
        await this.fetchInventoryAdvancePaymentCommentsMentionableUsers(
          this.inventoryAdvancePayment.id
        );
      } catch (error) {
        this.commentErrorMessage = setErrorFromAxiosError(
          error as AxiosError
        ).message;
      } finally {
        this.mentionsLoading = false;
      }
    },

    toggleEditingComment() {
      if (!this.isEditing) {
        // To reset the selected requisition comment data to the original when rediting
        this.selectedComment = cloneDeep(this.inventoryAdvancePaymentComment);
        this.commentTextHtml = this.inventoryAdvancePaymentComment.getTextHtml();
      }

      this.isEditing = !this.isEditing;
    },

    stopEditingComment() {
      if (!this.selectedComment.text || this.textHasDifference()) {
        this.isEditing = false;
      }
    },

    addBaseAttachments(files: File[]) {
      this.attachments.push(
        ...files.map(
          file => new InventoryAdvancePaymentCommentAttachment({ file: file })
        )
      );
    },

    cancelCommentAttachmentDeleteDialog() {
      this.deleteCommentAttachmentDialog = false;
    },

    openAttachmentDeleteDialog(attachment: IAttachment) {
      this.clearAllErrors();
      this.selectedAttachment = attachment;
      this.deleteCommentAttachmentDialog = true;
    },

    removeCommentAttachment(attachment: IAttachment) {
      this.clearAllErrors();
      this.isAttachmentDeleteInProgress = true;

      this.removeInventoryAdvancePaymentCommentAttachment({
        inventoryAdvancePaymentId: this.inventoryAdvancePayment.id,
        commentId: this.inventoryAdvancePaymentComment.id,
        attachmentId: attachment.id
      })
        .then(() => {
          this.deleteCommentAttachmentDialog = false;
        })
        .catch(error => {
          this.attachmentDeleteError = setErrorFromAxiosError(error);
        })
        .finally(() => {
          this.isAttachmentDeleteInProgress = false;
        });
    },

    downloadAttachmentFile(attachment: IAttachment) {
      this.clearAllErrors();
      this.$services.fileLoader
        .downloadFile(attachment.file as string)
        .then(data => {
          createFile(data, attachment.filename);

          this.createSnack({
            color: 'success darken-1',
            type: 'success',
            right: true,
            useBtn: false,
            msg: `File attachment download successfully`,
            btnText: 'Ok',
            timeout: 6000
          });
        })
        .catch(error => {
          const err = setErrorFromAxiosError(error);
          this.createSnack({
            color: 'error',
            type: 'error',
            right: true,
            useBtn: false,
            msg: `Failed to download Attachment: ${err.detail || err.message}`,
            btnText: 'Close'
          });
        });
    }
  }
});
